'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useFoundry } from '@/lib/foundry/context';

export function CreatePlanForm() {
  const router = useRouter();
  const { actions } = useFoundry();
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Plan name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const plan = await actions.createPlan({
        name: name.trim(),
        industry: industry.trim() || undefined,
      });
      router.push(`/tools/foundry/editor?id=${plan.id}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-foreground mb-2"
        >
          Plan Name *
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., My SaaS Startup 2025"
          className="w-full px-4 py-3 bg-background/60 border border-border rounded-lg text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent"
          disabled={loading}
        />
      </div>

      <div>
        <label
          htmlFor="industry"
          className="block text-sm font-medium text-foreground mb-2"
        >
          Industry (optional)
        </label>
        <select
          id="industry"
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          className="w-full px-4 py-3 bg-background/60 border border-border rounded-lg text-foreground focus:outline-none focus:border-accent"
          disabled={loading}
        >
          <option value="">Select an industry...</option>
          <option value="saas">SaaS / Software</option>
          <option value="ecommerce">E-commerce / Retail</option>
          <option value="fintech">Fintech / Financial Services</option>
          <option value="healthcare">Healthcare / Healthtech</option>
          <option value="edtech">Education / Edtech</option>
          <option value="marketplace">Marketplace</option>
          <option value="b2b_services">B2B Services</option>
          <option value="consumer">Consumer Products</option>
          <option value="other">Other</option>
        </select>
      </div>

      {error && (
        <div className="p-4 bg-red-500/20 border border-red-500/40 rounded-lg text-red-300">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? 'Creating...' : 'Create Business Plan'}
      </button>
    </form>
  );
}
